// src/components/scanner/QRReaderWrapper.js
import React, { useState, useEffect } from 'react';
import { QrReader } from 'react-qr-reader';
import './QRReaderWrapper.css';

/**
 * Composant wrapper pour QrReader qui gère les différents cas d'utilisation de la caméra
 */
const QRReaderWrapper = ({ onResult, onError, scanning = true }) => {
  const [cameraPermission, setCameraPermission] = useState('unknown');
  const [facingMode, setFacingMode] = useState('environment');
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [debug, setDebug] = useState('');
  const [videoVisible, setVideoVisible] = useState(true);
  const [usingNativeCamera, setUsingNativeCamera] = useState(false);
  const [cameraStartAttempts, setCameraStartAttempts] = useState(0);
  const [videoStream, setVideoStream] = useState(null);

  // Fonction pour démarrer manuellement la caméra
  const startCameraManually = async () => {
    try {
      setCameraStartAttempts(prev => prev + 1);
      
      // Arrêter tout stream précédent
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }
      
      // Options pour la caméra arrière
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      // Tentative d'obtenir un stream
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setVideoStream(stream);
      
      // Afficher le stream dans un élément vidéo
      const videoElement = document.getElementById('manual-video');
      if (videoElement) {
        videoElement.srcObject = stream;
        videoElement.play();
      }
      
      setDebug(prev => prev + '\nCaméra démarrée manuellement');
      setCameraPermission('granted');
      
      return true;
    } catch (error) {
      setDebug(prev => prev + '\nErreur au démarrage manuel: ' + error.message);
      console.error('Manual camera start failed:', error);
      return false;
    }
  };

  // Initialisation et vérification de la caméra
  useEffect(() => {
    // Vérifier la compatibilité du navigateur avec l'API mediaDevices
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraPermission('unsupported');
      onError({ name: 'NotSupportedError', message: 'Le navigateur ne supporte pas l\'accès à la caméra' });
      return;
    }
    
    const checkCamera = async () => {
      try {
        // Demander la permission d'accès à la caméra
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        
        // Vérifier qu'il y a bien des pistes vidéo
        const videoTracks = stream.getVideoTracks();
        setDebug('Camera stream obtenu: ' + videoTracks.length + ' pistes vidéo');
        
        if (videoTracks.length > 0) {
          videoTracks.forEach(track => {
            setDebug(prev => prev + '\nPiste: ' + track.label);
          });
          setCameraPermission('granted');
        } else {
          setDebug('Aucune piste vidéo trouvée');
          setCameraPermission('error');
        }
        
        // Libérer le stream de test
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        console.error('Camera access error:', error);
        setDebug('Erreur caméra: ' + error.name + ': ' + error.message);
        
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setCameraPermission('denied');
        } else {
          setCameraPermission('error');
        }
        onError(error);
      }
    };
    
    checkCamera();
    
    // Nettoyage lors du démontage du composant
    return () => {
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [onError]);

  // Fonction pour basculer entre les caméras avant et arrière
  const toggleCamera = () => {
    setFacingMode(prevFacingMode => 
      prevFacingMode === 'environment' ? 'user' : 'environment'
    );
    
    // Redémarrer la caméra si en mode manuel
    if (videoStream) {
      startCameraManually();
    }
  };

  // Fonction pour activer/désactiver la torche
  const toggleTorch = async () => {
    try {
      if (!videoStream) {
        setDebug(prev => prev + '\nPas de stream pour la torche');
        return;
      }
      
      const videoTrack = videoStream.getVideoTracks()[0];
      if (!videoTrack) {
        setDebug(prev => prev + '\nPas de piste vidéo pour la torche');
        return;
      }
      
      // Vérifier les capacités
      const capabilities = videoTrack.getCapabilities();
      if (!capabilities.torch) {
        setDebug(prev => prev + '\nTorche non supportée');
        return;
      }
      
      // Appliquer la contrainte
      await videoTrack.applyConstraints({
        advanced: [{ torch: !torchEnabled }]
      });
      
      setTorchEnabled(!torchEnabled);
      setDebug(prev => prev + '\nTorche ' + (!torchEnabled ? 'activée' : 'désactivée'));
    } catch (error) {
      console.error('Error toggling torch:', error);
      setDebug(prev => prev + '\nErreur torche: ' + error.message);
    }
  };

  // Basculer la visibilité de la vidéo
  const toggleVideoVisibility = () => {
    setVideoVisible(!videoVisible);
  };

  // Basculer vers la caméra native
  const switchToNativeCamera = () => {
    setUsingNativeCamera(true);
  };
  
  // Redémarrer la caméra
  const restartCamera = () => {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    
    startCameraManually();
  };

  // Gérer les différents états de permission
  if (cameraPermission === 'denied') {
    return (
      <div className="qr-reader-permission-denied">
        <div className="permission-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        </div>
        <h3>Accès à la caméra refusé</h3>
        <p>Pour scanner les QR codes, vous devez autoriser l'accès à la caméra.</p>
        <button 
          className="permission-button"
          onClick={() => window.location.reload()}
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (cameraPermission === 'unsupported') {
    return (
      <div className="qr-reader-unsupported">
        <div className="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h3>Caméra non supportée</h3>
        <p>Votre navigateur ne supporte pas l'accès à la caméra.</p>
        <p>Veuillez essayer un autre navigateur comme Chrome ou Firefox.</p>
      </div>
    );
  }

  if (cameraPermission === 'error') {
    return (
      <div className="qr-reader-error">
        <div className="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h3>Erreur de caméra</h3>
        <p>Une erreur est survenue lors de l'accès à la caméra.</p>
        <div style={{ marginBottom: '15px', fontSize: '12px', opacity: '0.7' }}>
          {debug && <pre>{debug}</pre>}
        </div>
        <button 
          className="permission-button"
          onClick={startCameraManually}
        >
          Démarrer manuellement
        </button>
        <button 
          className="permission-button"
          onClick={() => window.location.reload()}
          style={{ marginLeft: '10px', backgroundColor: '#333' }}
        >
          Recharger la page
        </button>
      </div>
    );
  }

  // Mode de caméra manuelle (pour résoudre le problème de l'écran noir)
  if (cameraPermission === 'granted' && cameraStartAttempts > 0) {
    return (
      <div className="qr-reader-container manual-camera">
        <div className="debug-panel" style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 100,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: '#fff',
          padding: '5px',
          fontSize: '10px',
          maxWidth: '80%',
          maxHeight: '100px',
          overflow: 'auto',
          borderRadius: '4px'
        }}>
          {debug}
        </div>
        
        <video 
          id="manual-video" 
          className="manual-video" 
          autoPlay 
          playsInline
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '70vh',
            objectFit: 'cover'
          }}
        />
        
        <div className="qr-reader-overlay">
          <div className="qr-reader-frame"></div>
          <div className="qr-reader-instructions">Placez le QR code dans le cadre</div>
        </div>
        
        <div className="manual-controls" style={{
          position: 'absolute',
          bottom: '20px',
          left: '0',
          right: '0',
          display: 'flex',
          justifyContent: 'center',
          gap: '10px'
        }}>
          <button 
            onClick={toggleCamera}
            className="control-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
          </button>
          
          <button 
            onClick={toggleTorch}
            className={`control-button ${torchEnabled ? 'active' : ''}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v8"></path>
              <path d="M12 18v4"></path>
              <path d="M4.93 4.93l5.66 5.66"></path>
              <path d="M13.41 13.41l5.66 5.66"></path>
              <path d="M2 12h8"></path>
              <path d="M18 12h4"></path>
              <path d="M4.93 19.07l5.66-5.66"></path>
              <path d="M13.41 10.59l5.66-5.66"></path>
            </svg>
          </button>
          
          <button 
            onClick={restartCamera}
            className="control-button"
            style={{ backgroundColor: '#B80E20' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6"></path>
              <path d="M3 12a9 9 0 0 1 15-6.7l3-3"></path>
              <path d="M3 22v-6h6"></path>
              <path d="M21 12a9 9 0 0 1-15 6.7l-3 3"></path>
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="qr-reader-container">
      {/* Informations de débogage */}
      {cameraPermission === 'granted' && (
        <div className="debug-panel" style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 100,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: '#fff',
          padding: '5px',
          fontSize: '10px',
          maxWidth: '80%',
          maxHeight: '100px',
          overflow: 'auto',
          borderRadius: '4px'
        }}>
          {debug}
        </div>
      )}

      {cameraPermission === 'granted' && !usingNativeCamera && cameraStartAttempts === 0 && (
        <>
          <div className="qr-reader" style={{display: videoVisible ? 'block' : 'none'}}>
            <QrReader
              onResult={scanning ? onResult : null}
              constraints={{ 
                facingMode: facingMode,
                width: { ideal: 1280 },
                height: { ideal: 720 }
              }}
              className="qr-reader"
              scanDelay={500}
              videoId="qr-video"
            />
          </div>
          
          <div className="qr-reader-overlay">
            <div className="qr-reader-frame"></div>
            <div className="qr-reader-instructions">Placez le QR code dans le cadre</div>
          </div>
          
          <div className="qr-reader-controls">
            <button 
              className="control-button camera-toggle"
              onClick={toggleCamera}
              title="Changer de caméra"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                <circle cx="12" cy="13" r="4"></circle>
                <path d="M17 2v4"></path>
                <path d="M21 6H17"></path>
              </svg>
            </button>
          </div>
          
          {/* Démarrage manuel de la caméra */}
          <button 
            onClick={startCameraManually}
            style={{
              position: 'absolute', 
              bottom: '20px', 
              left: '50%', 
              transform: 'translateX(-50%)',
              zIndex: 100,
              padding: '10px 15px',
              backgroundColor: '#B80E20',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '14px'
            }}
          >
            Résoudre l'écran noir
          </button>
        </>
      )}
      
      {/* Option de caméra native comme alternative */}
      {usingNativeCamera && (
        <div className="native-camera-fallback" style={{
          padding: '20px',
          textAlign: 'center'
        }}>
          <p style={{marginBottom: '20px', color: '#fff'}}>
            Utilisez l'appareil photo pour prendre une photo du QR code
          </p>
          <input 
            type="file" 
            accept="image/*" 
            capture="environment"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                // Ici normalement, vous analyseriez l'image pour le QR code
                // Mais pour simplifier, nous allons juste revenir à la caméra QR
                alert("Cette fonctionnalité n'est pas encore implémentée. Retour à la caméra QR.");
                setUsingNativeCamera(false);
              }
            }}
            style={{
              backgroundColor: '#fff',
              padding: '15px',
              borderRadius: '8px',
              width: '100%'
            }}
          />
          <button
            onClick={() => setUsingNativeCamera(false)}
            style={{
              marginTop: '20px',
              padding: '10px 15px',
              backgroundColor: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '5px'
            }}
          >
            Retour à la caméra QR
          </button>
        </div>
      )}
      
      {cameraPermission === 'unknown' && (
        <div className="qr-reader-loading">
          <div className="loading-spinner"></div>
          <p>Accès à la caméra en cours...</p>
        </div>
      )}
    </div>
  );
};

export default QRReaderWrapper;